import {navigateTo} from '~/spa/navigate';
/**
 * The queue of popups a user has to go through on log in: the policy acceptance, the visibility
 * changes notice, the "add your name / avatar / story" requests and the weak password warning.
 *
 * Only one of them is on screen at a time. Each step, once it is closed, hands over to
 * `showNextRequiredPopup`, which shows the first required step after the one that just finished.
 */
import { simplePopupFactory } from 'shared/simple_popups/simplePopupFactory';
import { showDimmer, hideDimmer } from 'shared/utils/dimmer';
import { CurrentUserClass } from 'shared/CurrentUser';
import globalEventBus, { GLOBAL_EVENTS } from 'shared/globalEventBus';
import notifications from '~/vue/plugins/notifications';
import addMedia from '~/angular/app/add_media/addMedia';
import VisibilityChangesAcceptPopup from './visibility_changes_accept_popup/VisibilityChangesAcceptPopup.vue';
import SetFirstLastNamePopup from './SetFirstLastNamePopup.vue';
import AddProfileInfoRequestPopup from './AddProfileInfoRequestPopup.vue';
import ImprovePasswordWarning from './ImprovePasswordWarning.vue';
import GDPRPopup from './GDPRPopup.vue';
import userprofileResourceImport from '../userprofile/userprofile_resource';
import userprofileSettingsResourceImport from '../userprofile/userprofile_settings_resource';

const UserProfileResource = userprofileResourceImport();
const UserProfileSettingsResource = userprofileSettingsResourceImport();

const POLICY_RETRY_DELAY = 1000 * 60 * 5;

let CurrentUser;
let steps = [];

function showNextRequiredPopup(fromIndex = 0) {
    for (let i = fromIndex; i < steps.length; i++) {
        if (steps[i].required) {
            steps[i].show();
            return;
        }
    }
}

// the index of a step is where the next lookup starts once that step is done
function stepDone(step) {
    showNextRequiredPopup(steps.indexOf(step) + 1);
}

function showPolicyPopup() {
    const { vm, popupComponent } = simplePopupFactory.mount_vue_popup(GDPRPopup);
    popupComponent.show()
        .then(acceptPolicy, () => {
            setTimeout(showPolicyPopup, POLICY_RETRY_DELAY);
            stepDone(policyStep);
        })
        .finally(() => {
            vm?.$destroy();
        });
}

function acceptPolicy() {
    showDimmer();
    return UserProfileResource.sign_policy(CurrentUser.id)
        .then(resp => {
            CurrentUser.is_policy_signed = true;
            notifications.success('Continue');
            hideDimmer();
            if (CurrentUser.email) {
                return simplePopupFactory.show_popup('Thanks!', 'Thanks, a confirmation email has been sent to you', 'OK');
            }
        }, err => {
            hideDimmer();
            notifications.error(err || 'Error');
            setTimeout(showPolicyPopup, POLICY_RETRY_DELAY);
        })
        .catch(err => console.error(err))
        .finally(() => {
            stepDone(policyStep);
        });
}

function showVisibilityChangesPopup() {
    const { vm, popupComponent } = simplePopupFactory.mount_vue_popup(VisibilityChangesAcceptPopup);
    popupComponent.show(CurrentUser.id, !CurrentUser.is_searchable)
        .finally(() => {
            signVisibilityChanges();
            vm?.$destroy();
            stepDone(visibilityChangesStep);
        });
}

function signVisibilityChanges() {
    UserProfileResource.sign_visibility_changes(CurrentUser.id)
        .then(resp => {
            CurrentUser.visibility_changes_signed = true;
        });
}

function showAddNamePopup() {
    const { vm, popupComponent } = simplePopupFactory.mount_vue_popup(SetFirstLastNamePopup);
    popupComponent.show()
        .then(({ first_name, last_name }) => {
            showDimmer();
            CurrentUser.first_name = first_name;
            CurrentUser.last_name = last_name;
            return UserProfileSettingsResource.update_first_and_last_name(CurrentUser.id, { first_name, last_name })
                .then(resp => {
                    notifications.success('Updated');
                    globalEventBus.emit(GLOBAL_EVENTS.PROFILE_DATA_CHANGED);
                });
        }, cancel => UserProfileSettingsResource.postpone_first_and_last_name_update()
            .then(resp => {
                notifications.success('We will remind you on next log in');
            }))
        .finally(() => {
            hideDimmer();
            vm?.$destroy();
            stepDone(addNameStep);
        });
}

function showAddAvatarPopup() {
    addMedia
        .addAvatarByCrop(
            'Add your profile Pic!',
            'Please add a profile picture so people can easily identify you on Multi Me',
            'Remind me later',
            !CurrentUser.is_my_media_empty
        )
        .then(avatarBase64value => {
            showDimmer();
            return UserProfileResource.update_avatar_by_crop(CurrentUser.id, avatarBase64value)
                .then(resp => {
                    CurrentUser.avatar = resp.data.avatar;
                    globalEventBus.emit(GLOBAL_EVENTS.MY_AVATAR_CHANGED, resp.data.avatar);
                    notifications.success('Avatar changed');
                }, err => {
                    notifications.error(err || 'Error');
                });
        }, cancel => UserProfileSettingsResource.postpone_add_avatar()
            .then(resp => {
                notifications.success('We will remind you on next log in');
            }))
        .finally(() => {
            hideDimmer();
            stepDone(addAvatarStep);
        });
}

function showFillProfileStoryRequestPopup() {
    const { vm, popupComponent } = simplePopupFactory.mount_vue_popup(AddProfileInfoRequestPopup);
    popupComponent.show()
        .then(yes => {
        }, cancel => UserProfileSettingsResource.postpone_fill_profile_story()
            .then(resp => {
                notifications.success('We will remind you on next log in');
            }))
        .finally(() => {
            vm?.$destroy();
            stepDone(fillProfileStoryStep);
        });
}

function showImprovePasswordWarningPopup() {
    const { vm, popupComponent } = simplePopupFactory.mount_vue_popup(ImprovePasswordWarning);
    popupComponent.show()
        .then(res => {
            if (res === 'postpone') {
                return UserProfileSettingsResource.postpone_weak_password_reminder(CurrentUser.id)
                    .then(resp => {
                        notifications.success('Postponed');
                    });
            } else if (res === 'open_profile_settings') {
                navigateTo('/users/me/settings?at=2');
            }
        }, err => {
        })
        .finally(() => {
            vm?.$destroy();
            stepDone(weakPasswordStep);
        });
}

const policyStep = { required: false, show: showPolicyPopup };
const visibilityChangesStep = { required: false, show: showVisibilityChangesPopup };
const addNameStep = { required: false, show: showAddNamePopup };
const addAvatarStep = { required: false, show: showAddAvatarPopup };
const fillProfileStoryStep = { required: false, show: showFillProfileStoryRequestPopup };
const weakPasswordStep = { required: false, show: showImprovePasswordWarningPopup };

/**
 * Entry point, called once per page load (see index.js).
 */
function runGDPRChecks() {
    CurrentUser = new CurrentUserClass();
    steps = [policyStep, visibilityChangesStep, addNameStep, addAvatarStep, fillProfileStoryStep, weakPasswordStep];

    if (!CurrentUser.is_authenticated()) {
        return;
    }

    policyStep.required = !CurrentUser.is_policy_signed;
    visibilityChangesStep.required = !CurrentUser.visibility_changes_signed && !CurrentUser.is_circle_user();
    addNameStep.required = !CurrentUser.first_and_last_name_set_postponed && !CurrentUser.first_name && !CurrentUser.last_name;
    addAvatarStep.required = !CurrentUser.add_avatar_postponed && (CurrentUser.avatar.indexOf('/static/') !== -1);
    fillProfileStoryStep.required = !CurrentUser.fill_profile_story_postponed && CurrentUser.is_my_story_empty
        && (document.location.pathname !== '/users/me/profile');
    weakPasswordStep.required = CurrentUser.password_marked_as_weak && !CurrentUser.weak_password_reminder_postponed
        && (document.location.pathname !== '/users/me/settings');

    showNextRequiredPopup();
}

export default runGDPRChecks;
export { runGDPRChecks };
